import { GlassCard } from '@/components/design-system';
import { Progress } from '@/components/ui/progress';
import { createAuthenticatedApiClient } from '@/lib/api-client';
import { uploadAppFiles, getAppFiles } from '@/lib/collections/appFiles';
import { generateRandomId } from '@/lib/utils';
import { resizeImageFile } from '@/utils/image-resize';
import {
  formatCentsAsUsd,
  isPlasticType,
  PLASTIC_TYPE_LABELS,
  type SmartScanResult,
} from '@/utils/plastic-rates';
import { getIdToken, useAuth } from '@pooflabs/web';
import { AnimatePresence, motion } from 'framer-motion';
import { Camera, RotateCcw, ScanLine, Sparkles } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

interface SmartScanPanelProps {
  onScanComplete: (result: SmartScanResult, photoUrl: string | undefined, previewUrl: string) => void;
  /** Bump this number from the parent to clear the panel back to idle. */
  resetToken: number;
}

type Phase = 'idle' | 'scanning' | 'done';

/**
 * The showpiece of the household dashboard — snap/upload a photo of plastic
 * waste, watch an amber scan-line sweep over it while the AI identifies the
 * plastic type and estimates weight + worth, then reveal the result.
 */
export function SmartScanPanel({ onScanComplete, resetToken }: SmartScanPanelProps) {
  const { user } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const lastResetToken = useRef(resetToken);

  const [phase, setPhase] = useState<Phase>('idle');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [result, setResult] = useState<SmartScanResult | null>(null);

  if (lastResetToken.current !== resetToken) {
    lastResetToken.current = resetToken;
    if (phase !== 'idle') {
      setPhase('idle');
      setPreviewUrl(null);
      setResult(null);
    }
  }

  const runScan = async (file: File) => {
    if (!user?.address) {
      toast.error('Connect your wallet to scan.');
      return;
    }

    setPhase('scanning');
    setResult(null);

    try {
      const { blob, dataUrl, mediaType } = await resizeImageFile(file);
      setPreviewUrl(dataUrl);

      const token = await getIdToken();
      const authApi = createAuthenticatedApiClient(token ?? '', user.address);

      const uploadFileId = generateRandomId();
      const uploadFile = new File([blob], `${uploadFileId}.jpg`, { type: mediaType });

      const [scanResult, uploadOk] = await Promise.all([
        authApi.post<SmartScanResult>('/api/smart-scan', { imageBase64: dataUrl, mediaType }),
        uploadAppFiles(uploadFileId, uploadFile).catch(() => false),
      ]);

      let photoUrl: string | undefined;
      if (uploadOk) {
        const uploaded = await getAppFiles(uploadFileId);
        photoUrl = uploaded?.url;
      }

      const plasticType = isPlasticType(scanResult.plasticType) ? scanResult.plasticType : 'mixed';
      const finalResult: SmartScanResult = { ...scanResult, plasticType };

      setResult(finalResult);
      setPhase('done');
      onScanComplete(finalResult, photoUrl, dataUrl);
    } catch (err) {
      console.error('Smart Scan failed', err);
      toast.error(err instanceof Error ? err.message : 'Smart Scan failed. Try again.');
      setPhase('idle');
      setPreviewUrl(null);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void runScan(file);
    e.target.value = '';
  };

  const retake = () => {
    setPhase('idle');
    setPreviewUrl(null);
    setResult(null);
  };

  return (
    <GlassCard padding="lg" className="relative overflow-hidden border-[hsl(var(--amber))]/30">
      <div className="pointer-events-none absolute -top-24 right-0 h-64 w-64 rounded-full bg-[hsl(var(--amber))]/15 blur-3xl" />

      <div className="relative flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[hsl(var(--amber))]/15 ring-1 ring-[hsl(var(--amber))]/40">
          <Sparkles className="h-4 w-4 text-[hsl(var(--amber))]" strokeWidth={2.2} />
        </span>
        <div>
          <span className="text-overline text-[hsl(var(--amber))]">Smart Scan</span>
          <h2 className="font-heading text-xl font-bold text-foreground">See what it&apos;s worth</h2>
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        className="hidden"
      />

      <div className="relative mt-5">
        <AnimatePresence mode="wait">
          {phase === 'idle' && (
            <motion.button
              key="idle"
              type="button"
              onClick={() => inputRef.current?.click()}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex w-full flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-[hsl(var(--amber))]/30 bg-[hsl(var(--amber))]/[0.04] py-14 text-center transition-colors hover:border-[hsl(var(--amber))]/60 hover:bg-[hsl(var(--amber))]/[0.07]"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--amber))]/15 ring-1 ring-[hsl(var(--amber))]/40">
                <Camera className="h-6 w-6 text-[hsl(var(--amber))]" strokeWidth={2} />
              </span>
              <span className="font-semibold text-foreground">Snap or upload a photo</span>
              <span className="max-w-[16rem] text-sm text-muted-foreground">
                Point your camera at the plastic you&apos;ve recovered.
              </span>
            </motion.button>
          )}

          {phase === 'scanning' && previewUrl && (
            <motion.div
              key="scanning"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="relative overflow-hidden rounded-2xl"
            >
              <img src={previewUrl} alt="Scanning" className="h-72 w-full object-cover" />
              <div className="absolute inset-0 bg-background/30" />

              {/* Corner brackets — viewfinder feel */}
              {(['top-3 left-3', 'top-3 right-3', 'bottom-3 left-3', 'bottom-3 right-3'] as const).map((pos) => (
                <span
                  key={pos}
                  className={`absolute h-6 w-6 border-[hsl(var(--amber))] ${pos} ${
                    pos.includes('top') ? 'border-t-2' : 'border-b-2'
                  } ${pos.includes('left') ? 'border-l-2' : 'border-r-2'}`}
                />
              ))}

              {/* Sweeping scan line */}
              <motion.div
                className="absolute inset-x-0 h-1 bg-gradient-to-r from-transparent via-[hsl(var(--amber))] to-transparent shadow-[0_0_20px_2px_hsl(var(--amber))]"
                initial={{ top: '0%' }}
                animate={{ top: ['0%', '96%', '0%'] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
              />

              <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 bg-gradient-to-t from-background/90 to-transparent py-4">
                <ScanLine className="h-4 w-4 animate-pulse text-[hsl(var(--amber))]" />
                <span className="text-sm font-medium text-[hsl(var(--amber))]">
                  Analyzing plastic&hellip;
                </span>
              </div>
            </motion.div>
          )}

          {phase === 'done' && result && previewUrl && (
            <motion.div
              key="done"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="space-y-4"
            >
              <div className="relative overflow-hidden rounded-2xl">
                <img src={previewUrl} alt="Scanned plastic" className="h-56 w-full object-cover" />
                <button
                  type="button"
                  onClick={retake}
                  className="absolute right-3 top-3 flex items-center gap-1.5 rounded-full bg-background/80 px-3 py-1.5 text-xs font-medium text-foreground backdrop-blur-md transition-colors hover:bg-background"
                >
                  <RotateCcw className="h-3 w-3" /> Retake
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-border/60 bg-secondary/30 p-4">
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">Detected</span>
                  <p className="mt-1 font-heading text-lg font-bold text-foreground">
                    {PLASTIC_TYPE_LABELS[result.plasticType]}
                  </p>
                </div>
                <div className="rounded-xl border border-border/60 bg-secondary/30 p-4">
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">Est. weight</span>
                  <p className="mt-1 font-heading text-lg font-bold text-foreground">{result.estimatedKg} kg</p>
                </div>
              </div>

              <div className="rounded-xl bg-gradient-to-br from-[hsl(var(--amber))]/15 to-[hsl(var(--amber))]/5 p-4 ring-1 ring-[hsl(var(--amber))]/30">
                <span className="text-xs uppercase tracking-wide text-[hsl(var(--amber))]">Estimated worth</span>
                <p className="mt-1 font-heading text-3xl font-bold text-[hsl(var(--amber))]">
                  {formatCentsAsUsd(result.estimatedValue)}
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Confidence</span>
                  <span>{Math.round(result.confidence * 100)}%</span>
                </div>
                <Progress
                  value={Math.round(result.confidence * 100)}
                  className="mt-1.5 h-1.5 [&>div]:bg-[hsl(var(--amber))]"
                />
              </div>

              {result.note && <p className="text-sm italic text-muted-foreground">&ldquo;{result.note}&rdquo;</p>}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </GlassCard>
  );
}

export default SmartScanPanel;
